/**
 * Existing-student shape + mappers for the Add-to-billing flow. Students are
 * read live from the org; `studentToExistingStudent` maps a real CCMS student
 * doc into the shape the picker/billing steps need.
 *
 * @handoff `alreadyBilled` is proxied by `enrollment.billingPlan`; for exact
 * accuracy cross-check active recurring plans (`fetchOrganizationInvoicePlans`).
 */
import { blankRow, RosterRow } from '../AddFamilies/types';

export interface ExistingStudent {
  id: string;
  childFirstName: string;
  childLastName: string;
  childBirthdate: string;
  parentFirstName: string;
  parentLastName: string;
  parentEmail: string;
  room: string;
  // Real room name for display (real students carry a room id, not one of our
  // fixed ROOM_OPTIONS values).
  roomName?: string;
  // True when the student is already on an active recurring plan. Shown but
  // locked in the picker so we never double-bill.
  alreadyBilled: boolean;
}

/** Normalize a Firestore Timestamp / Date / string birthday to an ISO date. */
export function toISODate(value: any): string {
  if (!value) return '';
  try {
    const d = typeof value?.toDate === 'function' ? value.toDate() : value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

/**
 * Map a real CCMS student doc to the shape this flow needs. The parent is the
 * first family member with relationship "parent" (falling back to any family
 * member), matching how the wizard writes contacts. `alreadyBilled` is derived
 * from an existing recurring plan on the enrollment.
 *
 * @handoff `alreadyBilled` here just checks `enrollment.billingPlan`; when
 * wiring for real, cross-check against active `fetchOrganizationInvoicePlans`.
 */
export function studentToExistingStudent(student: any, roomNameById: (id?: string) => string): ExistingStudent {
  const family = student?.family || {};
  const members = (student?.familyMembers || []).map((id: string) => family[id]).filter(Boolean);
  const parent = members.find((c: any) => c?.relationship === 'parent') || members[0] || Object.values(family)[0] || {};
  const roomId = student?.rooms?.[0] || '';
  return {
    id: student.id,
    childFirstName: student?.firstName || '',
    childLastName: student?.lastName || '',
    childBirthdate: toISODate(student?.birthday),
    parentFirstName: parent?.firstName || '',
    parentLastName: parent?.lastName || '',
    parentEmail: parent?.email || '',
    room: roomId,
    roomName: roomNameById(roomId),
    alreadyBilled: Boolean(student?.enrollment?.billingPlan),
  };
}

/**
 * Map a selected existing student onto the RosterRow shape the shared billing
 * step expects. Identity fields are already known (and read-only in that step);
 * tuition/subsidy start empty for the provider to fill in.
 */
export function toRosterRow(s: ExistingStudent): RosterRow {
  return {
    ...blankRow(),
    id: s.id,
    childFirstName: s.childFirstName,
    childLastName: s.childLastName,
    childBirthdate: s.childBirthdate,
    parentFirstName: s.parentFirstName,
    parentLastName: s.parentLastName,
    parentEmail: s.parentEmail,
    room: s.room,
  };
}
